import Typography from '@mui/material/Typography';
import InfoIcon from '@mui/icons-material/Info';
import Questions from 'components/Questions/Questions';
import { FormState, Loader, LoaderType, useQuestionsContext } from '@portal/ui-library';
import { PartnerIdProp } from '@portal/ui-library/dist/models';
import { Question } from '@portal/ui-library/dist/models/extension-generated';
import groupBy from 'lodash/groupBy';
import { Application, ConfigResponse } from 'models/extension-generated';
import { useTranslation } from 'react-i18next';
import { NoHealthQuestions } from './NoHealthQuestions/NoHealthQuestions';
import { IconButton } from '@mui/material';

export type HealthQuestionsFormProps = PartnerIdProp & {
  config: ConfigResponse;
  state: FormState<Application>;
  disabled?: boolean;
};

export function HealthQuestionsForm({ partnerId }: HealthQuestionsFormProps) {
  const { t } = useTranslation();

  const {
    isLoading,
    questions,
    getQuestionLabel,
    getQuestionText,
    getQuestionUnit,
    getQuestionMaxValue,
    getQuestionKey
  } = useQuestionsContext();

  const groupedQuestions = groupBy(questions, 'sections');

  return (
    <>
      {isLoading && (
        <Loader className="m-xl flex items-center justify-center" type={LoaderType.Circular} />
      )}
      {!isLoading && questions.length === 0 && <NoHealthQuestions />}
      {!isLoading && (
        <>
          {questions.length !== 0 && (
            <div
              aria-roledescription="information"
              aria-label={t('wizard.healthSection.info.text')}
              className="my-s p-[8px] bg-surface-60 gap-s flex items-center rounded-md"
            >
              <IconButton
                size="small"
                className="text-highlight-80"
                aria-describedby="health-info-text"
              >
                <InfoIcon />
              </IconButton>
              <span>
                <Typography id="health-info-text" variant="bodySMRegular" className="inline">
                  <strong>{t('wizard.healthSection.info.boldText')}</strong>
                  {t('wizard.healthSection.info.text')}
                </Typography>
              </span>
            </div>
          )}
          {Object.values(groupedQuestions).map((questions: Question[]) => {
            const id = questions[0].sections?.join(',');
            return (
              <div key={id}>
                <div className="my-s s:my-m">
                  <Typography
                    id={`${id}-legend`}
                    variant="headlineXXLGBold"
                    className="text-navigation-80"
                  >
                    {t(`wizard.healthSection.groups.${questions[0].sections}`)}
                  </Typography>
                </div>
                <fieldset aria-labelledby={`${id}-legend`}>
                  <Questions
                    headlineId={id}
                    partnerId={partnerId}
                    questions={questions}
                    isLoading={isLoading}
                    action={{
                      getQuestionLabel,
                      getQuestionText,
                      getQuestionUnit,
                      getQuestionMaxValue,
                      getQuestionKey
                    }}
                  />
                </fieldset>
              </div>
            );
          })}
        </>
      )}
    </>
  );
}
